import React from 'react'
import { Plus, Trash } from '@phosphor-icons/react'
import type { WorkflowNodeData, ABTestVariant } from '@/types/workflow'

interface ABTestConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, labelClass, labelStyle, sectionClass, infoBannerClass } from './_kit'
import { Field } from '@/components/ui/Field'

// Even split that always sums to 100 — leftover points go to the first variants
// (e.g. 3 variants → 34/33/33, 4 → 25/25/25/25, 5 → 20 each).
const distributeEvenly = (items: ABTestVariant[]): ABTestVariant[] => {
  const base = Math.floor(100 / items.length)
  const remainder = 100 - base * items.length
  return items.map((v, i) => ({ ...v, percentage: base + (i < remainder ? 1 : 0) }))
}

const ABTestConfig: React.FC<ABTestConfigProps> = ({ data, updateField }) => {
  const variants: ABTestVariant[] = data.ab_test_config?.variants || [
    { id: 'variant_1', label: 'Variant 1', percentage: 50 },
    { id: 'variant_2', label: 'Variant 2', percentage: 50 },
  ]

  const updateVariants = (newVariants: ABTestVariant[]) => {
    updateField('ab_test_config', { variants: newVariants })
  }

  const updateVariant = (index: number, field: keyof ABTestVariant, value: any) => {
    const updated = [...variants]
    updated[index] = { ...updated[index], [field]: value }
    updateVariants(updated)
  }

  const addVariant = () => {
    // Variant ids are edge sourceHandles, so a new id must never collide with an
    // existing one (even after earlier variants were removed).
    let n = variants.length + 1
    while (variants.some(v => v.id === `variant_${n}`)) n++
    const newVariant: ABTestVariant = {
      id: `variant_${n}`,
      label: `Variant ${variants.length + 1}`,
      percentage: 0,
    }
    updateVariants(distributeEvenly([...variants, newVariant]))
  }

  const removeVariant = (index: number) => {
    if (variants.length <= 2) return
    updateVariants(distributeEvenly(variants.filter((_, i) => i !== index)))
  }

  const totalPercentage = variants.reduce((sum, v) => sum + v.percentage, 0)

  // Names the variant list for AT. Generated, not hardcoded — an id must stay
  // unique even when more than one panel is mounted at once.
  const variantsLabelId = React.useId()

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Subject Line Test"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span id={variantsLabelId} className={labelClass} style={labelStyle}>Variants</span>
          <button
            type="button"
            onClick={addVariant}
            className="text-xs text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] flex items-center gap-1 transition-all duration-150"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="space-y-2" role="group" aria-labelledby={variantsLabelId}>
          {variants.map((variant, index) => (
            <div key={variant.id} className="flex items-center gap-2">
              <span className="w-6 shrink-0 text-xs font-emphasis text-muted-foreground">V{index + 1}</span>
              <input
                type="text"
                value={variant.label}
                onChange={(e) => updateVariant(index, 'label', e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 bg-card border border-border/60 rounded-lg text-sm text-foreground focus:border-[#7170ff]/55 transition-all duration-150"
              />
              <div className="relative w-20 shrink-0">
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={variant.percentage}
                  onChange={(e) => updateVariant(index, 'percentage', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 pr-7 bg-card border border-border/60 rounded-lg text-sm text-foreground focus:border-[#7170ff]/55 transition-all duration-150"
                />
                <span className="absolute right-2.5 top-2.5 text-xs text-muted-foreground">%</span>
              </div>
              {variants.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeVariant(index)}
                  className="p-1.5 shrink-0 text-muted-foreground hover:text-red-500 transition-all duration-150"
                >
                  <Trash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {totalPercentage !== 100 && (
          <p className="text-xs text-red-500 mt-1">Percentages must add up to 100% (currently {totalPercentage}%)</p>
        )}
      </div>

      <div className={infoBannerClass}>
        <p className="text-xs">
          Each variant creates a separate output path. Connect different email nodes to each variant to test different approaches.
        </p>
      </div>
    </div>
  )
}

export default ABTestConfig
